import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, ArrowLeft, ChevronRight } from 'lucide-react';
import {
  DEFAULT_DOC_SLUG,
  DOC_PRODUCTS,
  getProduct,
  getProductForSlug,
  type ProductId,
} from './manifest';
import { DocsScrollContext } from './DocsScrollContext';
import './DocsLayout.css';

export function DocsLayout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const initialSlug = slug ?? DEFAULT_DOC_SLUG;
  const [activeSlug, setActiveSlugState] = useState(initialSlug);
  const scrollRequestRef = useRef<(slug: string) => boolean>(() => false);

  const activeProduct = useMemo(
    () => getProductForSlug(activeSlug) ?? getProduct(DOC_PRODUCTS[0].id),
    [activeSlug]
  );

  const activeLocation = useMemo(() => {
    for (const section of activeProduct.sections) {
      const item = section.items.find(i => i.slug === activeSlug);
      if (item) return { sectionTitle: section.title, itemTitle: item.title };
    }
    return null;
  }, [activeSlug, activeProduct]);

  const setActiveSlug = useCallback((next: string) => {
    setActiveSlugState(prev => {
      if (prev === next) return prev;
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `/docs/${next}`);
      }
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({ activeSlug, setActiveSlug, scrollRequestRef }),
    [activeSlug, setActiveSlug]
  );

  const handleNavClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, targetSlug: string) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      if (scrollRequestRef.current(targetSlug)) {
        event.preventDefault();
        setActiveSlug(targetSlug);
        return;
      }
      event.preventDefault();
      navigate(`/docs/${targetSlug}`);
    },
    [navigate, setActiveSlug]
  );

  const handleProductSwitch = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, productId: ProductId) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      if (productId === activeProduct.id) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      navigate(`/docs/${getProduct(productId).defaultSlug}`);
    },
    [activeProduct.id, navigate]
  );

  return (
    <DocsScrollContext.Provider value={contextValue}>
      <div className="docs">
        <header className="docs__topbar">
          <Link to="/" className="docs__logo">
            <ArrowLeft size={14} aria-hidden="true" />
            Pryma
          </Link>
          <span className="docs__topbar-divider" aria-hidden="true" />
          <span className="docs__topbar-title">
            <BookOpen size={14} aria-hidden="true" />
            Docs
          </span>
          <nav className="docs__product-tabs" role="tablist" aria-label="Product">
            {DOC_PRODUCTS.map(product => {
              const isActive = product.id === activeProduct.id;
              return (
                <Link
                  key={product.id}
                  to={`/docs/${product.defaultSlug}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={e => handleProductSwitch(e, product.id)}
                  className={`docs__product-tab${isActive ? ' docs__product-tab--active' : ''}`}
                >
                  {product.label}
                </Link>
              );
            })}
          </nav>
          {activeLocation && (
            <nav className="docs__breadcrumbs" aria-label="Breadcrumb">
              <ChevronRight size={14} aria-hidden="true" className="docs__breadcrumb-sep" />
              <span className="docs__breadcrumb-section">{activeLocation.sectionTitle}</span>
              <ChevronRight size={14} aria-hidden="true" className="docs__breadcrumb-sep" />
              <span className="docs__breadcrumb-current" aria-current="page">
                {activeLocation.itemTitle}
              </span>
            </nav>
          )}
        </header>
        <div className="docs__body">
          <aside
            className="docs__sidebar"
            aria-label={`${activeProduct.label} navigation`}
            key={activeProduct.id}
          >
            <div className="docs__product-meta">
              <div className="docs__product-name">{activeProduct.label}</div>
              <p className="docs__product-tagline">{activeProduct.tagline}</p>
            </div>
            <nav>
              {activeProduct.sections.map(section => (
                <div key={section.title} className="docs__section">
                  <div className="docs__section-title">{section.title}</div>
                  <ul className="docs__section-list">
                    {section.items.map(item => {
                      const isActive = item.slug === activeSlug;
                      return (
                        <li key={item.slug}>
                          <Link
                            to={`/docs/${item.slug}`}
                            onClick={e => handleNavClick(e, item.slug)}
                            className={`docs__nav-link${isActive ? ' docs__nav-link--active' : ''}`}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {item.title}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>
          <main className="docs__content">
            <Outlet context={{ productId: activeProduct.id }} />
          </main>
        </div>
      </div>
    </DocsScrollContext.Provider>
  );
}
